import type { DateTime } from "luxon";
import {
	AgencySource,
	OUTREACH_LEVELS,
	type OutreachAgencyPost,
	type OutreachAllowed,
	OutreachAnswer,
	OutreachApproval,
	type OutreachEffort,
	OutreachExclusionFilter,
	OutreachLevel,
	type OutreachPaceReason,
	OutreachPaceReasonKind,
	type OutreachPlannedBatch,
	type OutreachPlannedCall,
	OutreachReachedKind,
	type OutreachReachedPerson,
	type OutreachScoredPerson,
	type OutreachSentBatch,
	OutreachStatus,
	OutreachUrgency,
	OutreachVia,
	type RequestOutreach,
} from "../../utils/models";
import type { OutreachPlanChoice } from "../useRequestOutreach";
import { byRank, byTime, callTimesOf, earlier, type LevelStarts, later, overtimeReach } from "./demoOutreachSeed";

/** DEMO ONLY (APP-933): the outreach plan as local state, and every action on it. Nothing here makes a network call. */

/** The agency post goes up only if at least this many spots are still open. */
const AGENCY_MIN_OPEN_SPOTS = 2;
/** Calls that start now begin on the next mark of this many minutes. */
const CALL_START_ROUNDING_MINUTES = 5;
/** What a raised level sends "sooner" goes out this long from now. */
const SOONER_HOURS = 1;
/** The Staffing pace reason: the target headcount, and how far under it is still "short" rather than below minimum. */
const STAFFING_TARGET = 4;
const STAFFING_SHORT_BY = 1;

export const ALL_ALLOWED: OutreachAllowed = { calls: true, overtime: true, agency: true };

/** What the demo stores; the call plan, the effort and the counts are derived from it (and the clock) on every read. */
export type DemoOutreach = Pick<RequestOutreach, "requestId" | "shift" | "sentBatches" | "plannedBatches" | "excludedGroups" | "skipped" | "signUps"> & {
	/** Running or Paused; Filled and Finished are derived. */
	paused: boolean;
	eligible: number;
};

/** The level the user set, when; `holding` stops the plan stepping up past it. */
type PlanOverride = { level: OutreachLevel; holding: boolean; at: DateTime };

/** A planned batch someone was skipped out of, so Undo can put them back, even once it has emptied. */
type SkippedHome = Pick<OutreachPlannedBatch, "id" | "via">;

export type DemoState = {
	outreach: DemoOutreach;
	automaticStarts: LevelStarts;
	override: PlanOverride | null;
	allowed: OutreachAllowed;
	/** Whether the department auto-approves sign-ups that fit its rules. */
	autoApprove: boolean;
	agency: OutreachAgencyPost;
	/** The planned batches each skipped person left; empty for someone who was only on the call list. */
	skippedFrom: ReadonlyMap<number, readonly SkippedHome[]>;
	sends: number;
};

export const initialAgencyOf = (neededCount: number, posted: DateTime | null): OutreachAgencyPost => ({
	source: AgencySource.Clipboard,
	// A one-person shift can never have two spots open, so its post waits for that one.
	minOpenSpots: Math.min(AGENCY_MIN_OPEN_SPOTS, neededCount),
	postedAt: posted,
});

const indexOf = (level: OutreachLevel): number => OUTREACH_LEVELS.indexOf(level);

/** When `level` starts once the user's pick is applied: levels up to it start by the time they set it, and holding drops the rest. */
const startOf = (state: DemoState, level: OutreachLevel): DateTime | null => {
	const automatic = state.automaticStarts[level];
	const { override } = state;
	if (override === null) return automatic;
	if (indexOf(level) <= indexOf(override.level)) return earlier(automatic, override.at);
	return override.holding ? null : automatic;
};

const lastStarted = (startsAt: (level: OutreachLevel) => DateTime | null, now: DateTime): OutreachLevel => {
	const level = [...OUTREACH_LEVELS].reverse().find((candidate) => {
		const at = startsAt(candidate);
		return at !== null && at <= now;
	});
	if (level === undefined) throw new Error("Demo outreach: no level has started");
	return level;
};

export const levelOf = (state: DemoState, now: DateTime): OutreachLevel => lastStarted((level) => startOf(state, level), now);
const automaticLevelOf = (state: DemoState, now: DateTime): OutreachLevel => lastStarted((level) => state.automaticStarts[level], now);
const reached = (state: DemoState, now: DateTime, level: OutreachLevel): boolean => indexOf(levelOf(state, now)) >= indexOf(level);

const callingNow = (state: DemoState, now: DateTime): boolean => state.allowed.calls && reached(state, now, OutreachLevel.Calls);
const viaNow = (state: DemoState, now: DateTime): OutreachVia => (callingNow(state, now) ? OutreachVia.Call : OutreachVia.Text);

const isText = (batch: OutreachPlannedBatch): boolean => batch.via === OutreachVia.Text;

/** Who's in line for a text, soonest first. */
const textQueueOf = (outreach: DemoOutreach): OutreachScoredPerson[] => outreach.plannedBatches.filter(isText).flatMap((batch) => batch.people);

const nextCallMarkOf = (now: DateTime): DateTime => {
	const minute = Math.ceil((now.minute + 1) / CALL_START_ROUNDING_MINUTES) * CALL_START_ROUNDING_MINUTES;
	return now.startOf("hour").plus({ minutes: minute });
};

/**
 * Everyone still to be called: everyone in line or already texted or alerted, minus the called, the skipped and whoever
 * already responded. Calls start with the Calls level (or now, when already calling) and speed up from the Overtime level.
 */
const callPlanOf = (state: DemoState, now: DateTime): OutreachPlannedCall[] => {
	const callsAt = startOf(state, OutreachLevel.Calls);
	if (!state.allowed.calls || callsAt === null) return [];
	const { outreach } = state;
	const sent = outreach.sentBatches.flatMap((batch) => batch.people);
	const left = new Set([
		...sent.filter((entry) => entry.via === OutreachVia.Call).map((entry) => entry.person.nurseId),
		...outreach.skipped.map((person) => person.nurseId),
		...outreach.signUps.map((signUp) => signUp.nurseId),
	]);
	const heard = sent.flatMap((entry) => (entry.kind === OutreachReachedKind.Scored && entry.via !== OutreachVia.Call ? [entry.person] : []));
	const planned = outreach.plannedBatches.flatMap((batch) => batch.people);
	const byId = new Map([...planned, ...heard].filter((person) => !left.has(person.nurseId)).map((person) => [person.nurseId, person]));
	const firstAt = callingNow(state, now) ? nextCallMarkOf(now) : later(callsAt, nextCallMarkOf(now));
	return callTimesOf([...byId.values()], firstAt, startOf(state, OutreachLevel.Overtime));
};

/** Who "Text/Call now" draws from: the call list while calling, the text queue otherwise. */
const nextUpOf = (state: DemoState, now: DateTime): OutreachScoredPerson[] =>
	callingNow(state, now) ? callPlanOf(state, now).map((call) => call.person) : textQueueOf(state.outreach);

/** Takes `nurseIds` out of the planned batches `touches` picks, dropping any batch left empty. */
const withoutPlanned = (
	batches: readonly OutreachPlannedBatch[],
	nurseIds: ReadonlySet<number>,
	touches: (batch: OutreachPlannedBatch) => boolean,
): OutreachPlannedBatch[] =>
	batches
		.map((batch) => (touches(batch) ? { ...batch, people: batch.people.filter((person) => !nurseIds.has(person.nurseId)) } : batch))
		.filter((batch) => batch.people.length > 0);

/**
 * Records a send of `people` as one new batch at the top, and takes them out of the text queue and the left-out groups.
 * `byUser`: a manual send (counted in `sends`), not one the plan made on its own.
 */
const send = (state: DemoState, people: OutreachReachedPerson[], now: DateTime, byUser: boolean): DemoState => {
	if (people.length === 0) return state;
	const ids = new Set(people.map((entry) => entry.person.nurseId));
	const { outreach } = state;
	const overrides = people.filter((entry) => entry.kind === OutreachReachedKind.Override).length;
	const id = byUser ? `you-${state.sends}` : `plan-${now.toMillis()}`;
	return {
		...state,
		sends: byUser ? state.sends + 1 : state.sends,
		outreach: {
			...outreach,
			sentBatches: [{ id, sentAt: now, byUser, people }, ...outreach.sentBatches],
			plannedBatches: withoutPlanned(outreach.plannedBatches, ids, isText),
			excludedGroups: outreach.excludedGroups
				.map((group) => ({ ...group, people: group.people.filter((person) => !ids.has(person.nurseId)) }))
				.filter((group) => group.people.length > 0),
			eligible: outreach.eligible + overrides,
		},
	};
};

/** A manual send hasn't been answered yet. */
const fresh = <T extends OutreachReachedPerson>(entry: T): T => ({ ...entry, answer: OutreachAnswer.NoAnswer });

export const sendPlanned = (state: DemoState, people: OutreachScoredPerson[], now: DateTime): DemoState =>
	send(
		state,
		people.map((person) => ({ kind: OutreachReachedKind.Scored, person, via: viaNow(state, now), answer: OutreachAnswer.NoAnswer })),
		now,
		true,
	);

export const textNow = (state: DemoState, nurseId: number, now: DateTime): DemoState =>
	sendPlanned(
		state,
		nextUpOf(state, now).filter((person) => person.nurseId === nurseId),
		now,
	);

/** Calls someone on the call list now, whether calls have started or not. */
export const callNow = (state: DemoState, nurseId: number, now: DateTime): DemoState => {
	const call = callPlanOf(state, now).find((candidate) => candidate.person.nurseId === nurseId);
	if (call === undefined) throw new Error(`Demo outreach: nurse ${nurseId} is not on the call list`);
	return send(state, [{ kind: OutreachReachedKind.Scored, person: call.person, via: OutreachVia.Call, answer: OutreachAnswer.NoAnswer }], now, true);
};

export const skip = (state: DemoState, nurseId: number, now: DateTime): DemoState => {
	const homes = state.outreach.plannedBatches.filter((batch) => batch.people.some((person) => person.nurseId === nurseId));
	// Someone already texted or alerted is only on the call list; skipping them just takes them off it.
	const candidates = homes.length > 0 ? homes[0].people : callPlanOf(state, now).map((call) => call.person);
	const person = candidates.find((candidate) => candidate.nurseId === nurseId);
	if (person === undefined) throw new Error(`Demo outreach: nurse ${nurseId} is not up next`);
	return {
		...state,
		skippedFrom: new Map(state.skippedFrom).set(
			nurseId,
			homes.map((batch) => ({ id: batch.id, via: batch.via })),
		),
		outreach: {
			...state.outreach,
			plannedBatches: withoutPlanned(state.outreach.plannedBatches, new Set([nurseId]), () => true),
			skipped: [person, ...state.outreach.skipped],
		},
	};
};

/** Puts `person` back in each batch they left; one that has emptied since comes back at the front of the line. */
const restored = (
	batches: readonly OutreachPlannedBatch[],
	homes: readonly SkippedHome[],
	person: OutreachScoredPerson,
	now: DateTime,
): OutreachPlannedBatch[] => {
	const frontAt = batches.length > 0 ? batches[0].plannedAt : now.plus({ hours: SOONER_HOURS });
	let out = [...batches];
	for (const home of homes) {
		out = out.some((batch) => batch.id === home.id)
			? out.map((batch) => (batch.id === home.id ? { ...batch, people: [...batch.people, person].sort(byRank) } : batch))
			: [{ ...home, plannedAt: frontAt, people: [person] }, ...out];
	}
	return out.sort((a, b) => byTime(a.plannedAt, b.plannedAt));
};

export const undoSkip = (state: DemoState, nurseId: number, now: DateTime): DemoState => {
	const person = state.outreach.skipped.find((candidate) => candidate.nurseId === nurseId);
	const homes = state.skippedFrom.get(nurseId);
	if (person === undefined || homes === undefined) throw new Error(`Demo outreach: nurse ${nurseId} was not skipped`);
	const { outreach } = state;
	const skippedFrom = new Map(state.skippedFrom);
	skippedFrom.delete(nurseId);
	const skipped = outreach.skipped.filter((candidate) => candidate.nurseId !== nurseId);
	// With no batch to return to they were only on the call list; dropping them from `skipped` puts them back on it.
	return { ...state, skippedFrom, outreach: { ...outreach, skipped, plannedBatches: restored(outreach.plannedBatches, homes, person, now) } };
};

export const textAnyway = (state: DemoState, nurseId: number, now: DateTime): DemoState => {
	const group = state.outreach.excludedGroups.find((candidate) => candidate.canOverride && candidate.people.some((person) => person.nurseId === nurseId));
	const person = group?.people.find((candidate) => candidate.nurseId === nurseId);
	if (group === undefined || person === undefined) throw new Error(`Demo outreach: nurse ${nurseId} is not in an overridable group`);
	return send(
		state,
		// A text even while calling, as the button says: they're asked past a filter, not chased.
		[{ kind: OutreachReachedKind.Override, person, filter: group.filter, via: OutreachVia.Text, answer: OutreachAnswer.NoAnswer }],
		now,
		true,
	);
};

/** More texts, raised by hand: every text still in line, and the app alert, go out within the hour instead of over the coming days. */
const textsSooner = (state: DemoState, now: DateTime): DemoState => {
	const soon = now.plus({ hours: SOONER_HOURS });
	const { plannedBatches } = state.outreach;
	const people = plannedBatches.filter(isText).flatMap((batch) => batch.people);
	const others = plannedBatches.filter((batch) => !isText(batch)).map((batch) => ({ ...batch, plannedAt: earlier(batch.plannedAt, soon) }));
	const texts: OutreachPlannedBatch[] = people.length > 0 ? [{ id: `sooner-${state.sends}`, via: OutreachVia.Text, plannedAt: soon, people }] : [];
	return { ...state, outreach: { ...state.outreach, plannedBatches: [...texts, ...others].sort((a, b) => byTime(a.plannedAt, b.plannedAt)) } };
};

/** Calls off past the Calls level: everyone who'd have been called and hasn't been texted gets a text within the hour instead. */
const textsInsteadOfCalls = (state: DemoState, now: DateTime): DemoState => {
	const { outreach } = state;
	const texted = new Set(
		outreach.sentBatches.flatMap((batch) => batch.people.filter((entry) => entry.via === OutreachVia.Text).map((entry) => entry.person.nurseId)),
	);
	const queued = new Set(textQueueOf(outreach).map((person) => person.nurseId));
	const people = callPlanOf({ ...state, allowed: { ...state.allowed, calls: true } }, now)
		.map((call) => call.person)
		.filter((person) => !texted.has(person.nurseId) && !queued.has(person.nurseId));
	if (people.length === 0) return state;
	const batch: OutreachPlannedBatch = { id: `instead-${state.sends}`, via: OutreachVia.Text, plannedAt: now.plus({ hours: SOONER_HOURS }), people };
	return { ...state, outreach: { ...outreach, plannedBatches: [...outreach.plannedBatches, batch].sort((a, b) => byTime(a.plannedAt, b.plannedAt)) } };
};

/** The Overtime level reached: everyone daily overtime left out gets the offer now, from the plan like the seeded offers. */
const offerOvertime = (state: DemoState, now: DateTime): DemoState => {
	const people = state.outreach.excludedGroups.filter((group) => group.filter === OutreachExclusionFilter.DailyOvertime).flatMap((group) => group.people);
	return send(
		state,
		people.map((person) => fresh(overtimeReach(state.outreach.requestId, person, viaNow(state, now)))),
		now,
		false,
	);
};

const approvedCountOf = (outreach: DemoOutreach): number =>
	outreach.signUps.filter((signUp) => signUp.approval === OutreachApproval.ByManager || signUp.approval === OutreachApproval.Automatic).length;

const openSpotsOf = (outreach: DemoOutreach): number => outreach.shift.neededCount - approvedCountOf(outreach);

/** The Agency level reached: the post goes up now, if it hasn't and enough spots are still open. */
const postToAgency = (state: DemoState, now: DateTime): DemoState =>
	state.agency.postedAt === null && openSpotsOf(state.outreach) >= state.agency.minOpenSpots ? { ...state, agency: { ...state.agency, postedAt: now } } : state;

/** Nobody new hears from us while paused or once the shift is filled. */
const isAsking = (outreach: DemoOutreach): boolean => !outreach.paused && openSpotsOf(outreach) > 0;

/** What a reached level sends at once, while still asking: the overtime offers and the agency post. Each is a no-op when done. */
const sendsDue = (state: DemoState, now: DateTime): DemoState => {
	if (!isAsking(state.outreach)) return state;
	let next = state;
	if (reached(next, now, OutreachLevel.Overtime) && next.allowed.overtime) next = offerOvertime(next, now);
	if (reached(next, now, OutreachLevel.Agency) && next.allowed.agency) next = postToAgency(next, now);
	return next;
};

/**
 * What a change to the plan does at once: texts sooner when the user raises it past More texts ahead of the clock,
 * texts in place of calls that are off, then the sends a reached level makes (`sendsDue`).
 */
const settle = (state: DemoState, now: DateTime, before: OutreachLevel): DemoState => {
	let next = state;
	const belowMoreTexts = (level: OutreachLevel): boolean => indexOf(level) < indexOf(OutreachLevel.MoreTexts);
	if (belowMoreTexts(before) && belowMoreTexts(automaticLevelOf(next, now)) && reached(next, now, OutreachLevel.MoreTexts)) next = textsSooner(next, now);
	if (reached(next, now, OutreachLevel.Calls) && !next.allowed.calls) next = textsInsteadOfCalls(next, now);
	return sendsDue(next, now);
};

export const pause = (state: DemoState): DemoState => ({ ...state, outreach: { ...state.outreach, paused: true } });

/** Picks up where it left off, sending what the level reached while paused would have. */
export const resume = (state: DemoState, now: DateTime): DemoState => sendsDue({ ...state, outreach: { ...state.outreach, paused: false } }, now);

const decide = (state: DemoState, nurseId: number, approval: OutreachApproval, now: DateTime): DemoState => {
	if (!state.outreach.signUps.some((signUp) => signUp.nurseId === nurseId && signUp.approval === OutreachApproval.Waiting)) {
		throw new Error(`Demo outreach: nurse ${nurseId} has no sign-up waiting`);
	}
	const signUps = state.outreach.signUps.map((signUp) => (signUp.nurseId === nurseId ? { ...signUp, approval, decidedAt: now } : signUp));
	return { ...state, outreach: { ...state.outreach, signUps } };
};

export const approve = (state: DemoState, nurseId: number, now: DateTime): DemoState => {
	if (openSpotsOf(state.outreach) <= 0) throw new Error(`Demo outreach: request ${state.outreach.requestId} has no open spot to approve nurse ${nurseId} into`);
	return decide(state, nurseId, OutreachApproval.ByManager, now);
};
export const decline = (state: DemoState, nurseId: number, now: DateTime): DemoState => decide(state, nurseId, OutreachApproval.NotApproved, now);

/** Picking our level without holding is back to our plan; stepping below it always holds, since the clock would raise it again. */
export const adjustPlan = (state: DemoState, choice: OutreachPlanChoice, now: DateTime): DemoState => {
	const before = levelOf(state, now);
	const automatic = automaticLevelOf(state, now);
	const lowered = indexOf(choice.level) < indexOf(automatic);
	const override = choice.level === automatic && !choice.holding ? null : { level: choice.level, holding: choice.holding || lowered, at: now };
	return settle({ ...state, override, allowed: choice.allowed }, now, before);
};

export const resetPlan = (state: DemoState, now: DateTime): DemoState => settle({ ...state, override: null, allowed: ALL_ALLOWED }, now, levelOf(state, now));

const URGENCY_OF: Record<OutreachLevel, OutreachUrgency> = {
	[OutreachLevel.Text]: OutreachUrgency.Normal,
	[OutreachLevel.MoreTexts]: OutreachUrgency.Normal,
	[OutreachLevel.Calls]: OutreachUrgency.Urgent,
	[OutreachLevel.Overtime]: OutreachUrgency.Critical,
	[OutreachLevel.Agency]: OutreachUrgency.Critical,
};

/** The story behind the clock's urgency: how far off the shift is, how it usually fills, and the headcount. */
const paceReasonsOf = (urgency: OutreachUrgency, outreach: DemoOutreach, now: DateTime): OutreachPaceReason[] => {
	const daysBefore = Math.max(0, Math.floor(outreach.shift.start.diff(now, "days").days));
	const shortBy = urgency === OutreachUrgency.Normal ? 0 : urgency === OutreachUrgency.Urgent ? STAFFING_SHORT_BY : STAFFING_SHORT_BY + 1;
	return [
		{ kind: OutreachPaceReasonKind.LeadTime, daysBefore, speedsUp: urgency !== OutreachUrgency.Normal },
		{ kind: OutreachPaceReasonKind.FillHistory, hardToFill: urgency === OutreachUrgency.Critical, speedsUp: urgency === OutreachUrgency.Critical },
		{
			kind: OutreachPaceReasonKind.Staffing,
			scheduled: STAFFING_TARGET - shortBy,
			target: STAFFING_TARGET,
			minimum: STAFFING_TARGET - STAFFING_SHORT_BY,
			speedsUp: shortBy > 0,
		},
	];
};

const effortOf = (state: DemoState, now: DateTime): OutreachEffort => {
	const automaticLevel = automaticLevelOf(state, now);
	const urgency = URGENCY_OF[automaticLevel];
	return {
		level: levelOf(state, now),
		automaticLevel,
		setByUser: state.override !== null,
		holding: state.override === null ? false : state.override.holding,
		schedule: OUTREACH_LEVELS.map((level) => ({ level, startsAt: startOf(state, level) })),
		allowed: state.allowed,
		urgency,
		paceReasons: paceReasonsOf(urgency, state.outreach, now),
	};
};

/** People, not sends: someone texted and then called counts once. An app alert alone isn't asking them directly. */
const askedWhere = (batches: readonly OutreachSentBatch[], keep: (entry: OutreachReachedPerson) => boolean): number =>
	new Set(batches.flatMap((batch) => batch.people.filter((entry) => entry.via !== OutreachVia.AppAlert && keep(entry)).map((entry) => entry.person.nurseId)))
		.size;

const statusOf = (outreach: DemoOutreach, nobodyLeft: boolean): OutreachStatus => {
	if (approvedCountOf(outreach) >= outreach.shift.neededCount) return OutreachStatus.Filled;
	if (outreach.paused) return OutreachStatus.Paused;
	return nobodyLeft ? OutreachStatus.Finished : OutreachStatus.Running;
};

/** The stored outreach plus what's derived from it. While calling, calls replace the planned texts, so only the app alert stays planned. */
export const readOutreach = (state: DemoState, now: DateTime): RequestOutreach => {
	const { outreach } = state;
	const plannedCalls = callPlanOf(state, now);
	const plannedBatches = callingNow(state, now) ? outreach.plannedBatches.filter((batch) => !isText(batch)) : outreach.plannedBatches;
	const signedUp = new Set(outreach.signUps.map((signUp) => signUp.nurseId));
	return {
		requestId: outreach.requestId,
		asOf: now,
		shift: outreach.shift,
		status: statusOf(outreach, plannedBatches.length === 0 && plannedCalls.length === 0),
		effort: effortOf(state, now),
		agency: state.agency,
		autoApprove: state.autoApprove,
		counts: {
			eligible: outreach.eligible,
			asked: askedWhere(outreach.sentBatches, () => true),
			declined: askedWhere(outreach.sentBatches, (entry) => entry.answer === OutreachAnswer.Declined && !signedUp.has(entry.person.nurseId)),
			approved: approvedCountOf(outreach),
		},
		sentBatches: outreach.sentBatches,
		plannedBatches,
		plannedCalls,
		excludedGroups: outreach.excludedGroups,
		skipped: outreach.skipped,
		signUps: outreach.signUps,
	};
};
